import React, { useEffect, useState } from 'react';
import { 
  Card, Form, Switch, InputNumber, Button, message, Skeleton, 
  Row, Col, Tabs, Space,
  Typography,
  Select,
  Alert,
  Statistic,
  Divider,
  Input,
  Tooltip,
  Badge
} from 'antd';
import {
  SaveOutlined, 
  CalendarOutlined,
  BellOutlined,
  QuestionCircleOutlined,
  MailOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  GiftOutlined,
  CalculatorOutlined,
  TagOutlined,
  PercentageOutlined,
  ExclamationCircleOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { CompanySettings } from '../../../types/company.types';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store/store';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import './settings.css';
import companyService from '../../../services/company/company.service';
import { handleApiError } from '../../../utils/errorHandler';
import FooterSection from '../../shared/components/footerSection';
import { OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import { fetchSubInfo } from '../../../store/slices/auth.slice';
import { all_routes } from '../../../router/all_routes';
import { useSubscription } from '../../../hooks/useSubscription';
import couponService from '../../../services/coupon/coupon.service';
import { showNotification } from '../auth/login/createCompanyPublic/registrationHelpers';
import subService from '../../../services/sub/sub.service';
import { useCompanyData } from '../../../hooks/useCompanyData';
import BillingAddressForm from '../../shared/components/BillingAddressForm';

import { productService, Product } from '../../../services/product/product.service';
import BasicInfoForm from './components/BasicInfoForm';
import TaxInfoForm from './components/TaxInfoForm';
import ExtraInfoForm from './components/ExtraInfoForm';
import CompanyStatsCard from './components/CompanyStatsCard';
import SmsSettingsForm from './components/SmsSettingsForm';
import NotificationSettingsForm from './components/NotificationSettingsForm';
import EmailSettingsForm from './components/EmailSettingsForm';
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const CompanySettingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shiftMode, setShiftMode] = useState<string>('');
  const { user, sub, subLoading } = useSelector((state: RootState) => state.auth);
  const [lateTolerance, setLateTolerance] = useState(false);
  const [fixedShiftDays, setFixedShiftDays] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('company-settings');
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Mobil durumu için state
  const routes = all_routes;
  const [searchParams] = useSearchParams(); // URL parametreleri için
  const [activeUsers, setActiveUsers] = useState<number>();
  // Fiyatlandırma ürünleri
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [pricingError, setPricingError] = useState<string | null>(null);

  // Subscription hook'u
  const {
    loading: subscriptionLoading,
    error: subscriptionError,
    createCheckoutSession,
    addUsers
  } = useSubscription();
  
  // Ödeme ayarları için yeni state'ler
  const [currentUserCount, setCurrentUserCount] = useState(0);
  const [newUserCount, setNewUserCount] = useState(1);
  const [additionalUserCount, setAdditionalUserCount] = useState(0); // Premium için ek kullanıcı sayısı
  const [showPriceCalculation, setShowPriceCalculation] = useState(false);
  
  // Kupon kodu state'leri
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  
  // Grace period için product seçimi
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // Billing address
  const [billingAddress, setBillingAddress] = useState<any>(null);
  const { companyData, loading: companyLoading } = useCompanyData();

  useEffect(() => {
    fetchSettings();
    // Sub bilgilerini yükle
    if (user?.companyId && !sub && !subLoading) {
      dispatch(fetchSubInfo(user.companyId));
    }
    // Pencere boyutu değiştiğinde mobil durumunu güncelle
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch, user?.companyId, sub, subLoading]);

  // Fiyatlandırma ürünlerini yükle
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setPricingError(null);
        const fetchedProducts = await productService.getAllProducts();
        setProducts(fetchedProducts);
      } catch (error) {
        setPricingError('Fiyatlandırma bilgileri yüklenemedi.');
        handleApiError(error, t);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, [t]);

  // URL parametresinden tab kontrolü
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams, activeTab]);

  // Sub bilgileri geldiğinde kullanıcı sayısını güncelle
  useEffect(() => {
    if (sub && sub.userLimit) {
      const userLimitNumber = parseInt(sub.userLimit.toString(), 10);
      setCurrentUserCount(userLimitNumber);
      setNewUserCount(userLimitNumber);
    }
  }, [sub]);

  useEffect(() => {
    const fetchActiveUsers = async () => {
      const response = await companyService.getCompanyActiveUsers(Number(user?.companyId));
      setActiveUsers(response.activeUserCount);
    };
    fetchActiveUsers();
  }, [user?.companyId]);


  const fetchSettings = async () => {
    try {
      if (!user?.companyId) return;
      const settings = await companyService.getCompanySettings(user.companyId);

      const formattedSettings = {
        ...settings,
        fixedShiftStartTime: settings.fixedShiftStartTime 
          ? moment(settings.fixedShiftStartTime, 'HH:mm') 
          : null,
        fixedShiftEndTime: settings.fixedShiftEndTime 
          ? moment(settings.fixedShiftEndTime, 'HH:mm') 
          : null,
        fixedShiftDays: settings.fixedShiftDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      };

      if (settings.shiftMode) {
        setShiftMode(settings.shiftMode);
        // Form alanına da string değer atıyoruz
        form.setFieldsValue({ ...formattedSettings, shiftMode: settings.shiftMode });
      } else {
        form.setFieldsValue(formattedSettings);
      }
      
      setFixedShiftDays(formattedSettings.fixedShiftDays);
      if (typeof settings.lateTolerance !== 'undefined') {
        setLateTolerance(settings.lateTolerance);
      }

      // Kullanıcı sayısı artık sub'dan alınıyor

    } catch (error: any) {
      handleApiError(error,t);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: CompanySettings) => {
    try {
      setSaving(true);
      if (!user?.companyId) return;
      
        const formattedValues = {
          ...values
        };
        await companyService.updateCompanySettings(user.companyId, formattedValues);
        message.success('Şirket bilgileri başarıyla güncellendi');
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setSaving(false);
    }
  };

  const handleShiftModeChange = (value: string) => {
    setShiftMode(value);
    form.setFieldValue('shiftMode', value);
  };

  if (loading || productsLoading || companyLoading || subLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Card>
            <Skeleton active paragraph={{ rows: 10 }} />
          </Card>
        </div>
      </div>
    );
  }

  // Yükleme sonrası veri kontrolü
  if (!companyData || !sub) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ textAlign: 'center', paddingTop: '50px' }}>
          <Alert
            message="Veri Yüklenemedi"
            description="Şirket veya abonelik bilgileri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyerek tekrar deneyin."
            type="error"
            showIcon
          />
        </div>
      </div>
    );
  }


  const renderFormActions = () => (
    <div className="settings-actions">
      <Form.Item>
        {activeTab === "grace-period-payment" || activeTab === "payment-settings" ? (
          <></>
        ): 
        (  
        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          icon={<SaveOutlined />}
          size="large"
          className="save-settings-btn"
        >
          {t('companySettings.saveSettings')}
        </Button>
        )}
      </Form.Item>
    </div>
  );

  const renderTooltip = (text: string) => (
    <OverlayTrigger
      placement="top"
      overlay={<BootstrapTooltip id={`tooltip-${text.substring(0, 10)}`}>{text}</BootstrapTooltip>}
    >
      <QuestionCircleOutlined className="info-icon" style={{ marginLeft: 4, cursor: 'pointer' }} />
    </OverlayTrigger>
  );

  // Yeni paket yapısına uygun fiyat hesaplama - proportional (orantılı) fiyatlandırma
  const calculatePricing = (userCount: number) => {
    if (products.length === 0) {
      return {
        monthly: 0, annual: 0, monthlyWithTax: 0, annualWithTax: 0,
        savings: 0, tax: { monthly: { amount: 0, total: 0 }, annual: { amount: 0, total: 0 } },
        userCount, monthlyUserPrice: 0, annualDiscountPercent: 0,
        proportionalPrice: 0, proportionalPriceWithTax: 0,
        remainingMonths: 0, remainingDays: 0
      };
    }

    // Sub'dan duration bilgisini al ve o duration'a ait ürünü bul
    const subDuration = sub?.duration || 3; // Default 3 ay
    const productForDuration = products.find(p => p.durationMonths === subDuration && p.isActive);
    const activeProduct = productForDuration || products.find(p => p.isActive);
    
    if (!activeProduct) {
      return {
        monthly: 0, annual: 0, monthlyWithTax: 0, annualWithTax: 0,
        savings: 0, tax: { monthly: { amount: 0, total: 0 }, annual: { amount: 0, total: 0 } },
        userCount, monthlyUserPrice: 0, annualDiscountPercent: 0,
        proportionalPrice: 0, proportionalPriceWithTax: 0,
        remainingMonths: 0, remainingDays: 0
      };
    }
    
    const monthlyUserPrice = activeProduct.pricePerUserPerMonth;
    const monthlyPrice = userCount * monthlyUserPrice;
    const annualPrice = monthlyPrice * 12 * 0.8; // %20 yıllık indirim
    
    // Orantılı fiyat hesaplaması (sadece premium kullanıcılar için)
    let proportionalPrice = 0;
    let remainingMonths = 0;
    let remainingDays = 0;
    
    if (sub?.status === 'active' && sub.expiresAt && sub.daysLeft) {
      const daysLeft = sub.daysLeft;
      
      // Mevcut aktif product'ı kullan (productId subscription'da yok)
      if (activeProduct && activeProduct.pricePerUserPerMonth) {
        // 1. Kullanılan paketin aylık kullanıcı fiyatını al
        const packageUserPrice = activeProduct.pricePerUserPerMonth;
        
        // 2. Paketin kaç aylık olduğunu bulup çarp
        const packageTotalPrice = packageUserPrice * activeProduct.durationMonths;
        
        // 3. Paket süresine göre toplam gün sayısını belirle ve günlük fiyatı hesapla
        let totalDaysInPackage = 0;
        if (activeProduct.durationMonths === 12) {
          totalDaysInPackage = 365;
        } else if (activeProduct.durationMonths === 6) {
          totalDaysInPackage = 180;
        } else if (activeProduct.durationMonths === 3) {
          totalDaysInPackage = 90;
        }
        
        // 4. Günlük fiyatı hesapla
        const dailyPrice = packageTotalPrice / totalDaysInPackage;
        
        // 5. Kalan gün sayısı ile günlük fiyatı çarp
        proportionalPrice = daysLeft * dailyPrice * userCount;
        // Debug için ay ve gün bilgilerini de hesapla
        remainingMonths = Math.floor(daysLeft / 30);
        remainingDays = daysLeft % 30;
      }
    }
    
    const taxRate = 0.20;
    const monthlyTaxAmount = monthlyPrice * taxRate;
    const annualTaxAmount = annualPrice * taxRate;
    const proportionalTaxAmount = proportionalPrice * taxRate;
    
    const monthlyWithTax = monthlyPrice + monthlyTaxAmount;
    const annualWithTax = annualPrice + annualTaxAmount;
    const proportionalPriceWithTax = proportionalPrice + proportionalTaxAmount;
    
    const savings = (monthlyPrice * 12) - annualPrice;
    const annualDiscountPercent = 0.2; // %20 indirim
    
    return {
      monthly: monthlyPrice,
      annual: annualPrice,
      monthlyWithTax: monthlyWithTax,
      annualWithTax: annualWithTax,
      savings: savings,
      tax: { 
        monthly: { amount: monthlyTaxAmount, total: monthlyWithTax },
        annual: { amount: annualTaxAmount, total: annualWithTax }
      },
      userCount,
      monthlyUserPrice,
      annualDiscountPercent,
      proportionalPrice,
      proportionalPriceWithTax,
      remainingMonths,
      remainingDays
    };
  };

  const currentPricing = calculatePricing(currentUserCount);
  const newPricing = calculatePricing(newUserCount);



  // ⚠️ CRITICAL: Sub durumuna göre tab görünürlüğü kontrolü
  const shouldShowPaymentTab = () => {
    if (!sub) return false;
    
    // Expired ise asla settings gösterme (zaten pricing'e yönlendirilir)
    if (sub.status === 'expired' || sub.status === 'inactive') return false;
    
    // Trial kullanıcılar için premium plan tanıtım sekmesi
    if (sub.status === 'active' || sub.status === 'trial') return true;
    
    return false;
  };
  // Premium kullanıcılar için ek kullanıcı sayısı değişimi
  const handleAdditionalUserCountChange = (value: number | null) => {
    if (value !== null && value >= 0) {
      // Premium kullanıcılar için kupon anlık güncelleme
      if (appliedCoupon && value !== additionalUserCount) {
        // Premium için proportional fiyat hesaplama
        const additionalUserPricing = calculatePricing(value);
        const newAmount = additionalUserPricing.proportionalPrice;
        
        if (appliedCoupon.couponType === 'percentage') {
          const discountAmount = newAmount * (appliedCoupon.couponValue / 100);
          const finalAmount = newAmount - discountAmount;
          
          setAppliedCoupon({
            ...appliedCoupon,
            originalAmount: newAmount,
            finalAmount: finalAmount,
            savings: discountAmount
          });
        } else {
          const discountAmount = Math.min(appliedCoupon.couponValue, newAmount);
          const finalAmount = Math.max(0, newAmount - discountAmount);
          
          setAppliedCoupon({
            ...appliedCoupon,
            originalAmount: newAmount,
            finalAmount: finalAmount,
            savings: discountAmount
          });
        }
      }
      setAdditionalUserCount(value);
      setShowPriceCalculation(value > 0);
    }
  };

  // Kupon uygulama fonksiyonu
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      showNotification('warning', 'Uyarı', 'Lütfen bir kupon kodu girin');
      return;
    }

    setCouponLoading(true);
    
    try {
      // Trial kullanıcılar için toplam fiyat, Premium kullanıcılar için ek kullanıcı fiyatı, Grace period için paket fiyatı
      let orderAmount = 0;
      const activeProduct = products.find(p => p.isActive);
      
      if (!activeProduct) {
        showNotification('error', 'Hata', 'Aktif fiyatlandırma bilgisi bulunamadı.');
        setCouponLoading(false);
        return;
      }
      
      if (sub?.status === 'trial') {
        // Trial kullanıcılar için product bazlı fiyat hesaplama
        if (!selectedProduct) {
          showNotification('warning', 'Uyarı', 'Kupon kullanmak için önce bir paket seçin');
          setCouponLoading(false);
          return;
        }
        
        // Trial için seçilen paket fiyatı (KDV hariç)
        orderAmount = newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
      } else if (sub?.status === 'active' && additionalUserCount > 0) {
        // Premium kullanıcılar için ek kullanıcı fiyatı - proportional hesaplama
        const additionalUserPricing = calculatePricing(additionalUserCount);
        orderAmount = additionalUserPricing.proportionalPrice;
      } else if (sub?.status === 'in_grace_period') {
        // Grace period için önce paket seçimi kontrolü
        if (!selectedProduct) {
          showNotification('warning', 'Uyarı', 'Kupon kullanmak için önce bir paket seçin');
          setCouponLoading(false);
          return;
        }
        
        // Grace period için seçilen paket fiyatı (KDV hariç)
        orderAmount = newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
      }

      if (orderAmount <= 0) {
        showNotification('warning', 'Uyarı', 'Geçerli bir tutar bulunmuyor');
        setCouponLoading(false);
        return;
      }

      if (!user?.companyId) {
        showNotification('error', 'Hata', 'Şirket bilgisi bulunamadı');
        setCouponLoading(false);
        return;
      }


      if (!selectedProduct && sub?.status !== 'active') {
        showNotification('error', 'Hata', 'Lütfen önce bir paket seçin');
        setCouponLoading(false);
        return;
      }
      
      const activeUsageProduct = products.find(p => sub?.duration === p.durationMonths)

      const response = await couponService.validateCoupon({
        code: couponCode.trim().toUpperCase(),
        orderAmount: orderAmount,
        companyId: user.companyId,
        productId: sub?.status === 'active' ? activeUsageProduct?.id! : selectedProduct?.id!,
        userCount: newUserCount
      });
      
      if (response.isValid) {
        // Kupon geçerli - fiyata uygula
        const appliedCouponData = {
          isValid: true,
          code: couponCode.trim().toUpperCase(),
          originalAmount: orderAmount,
          finalAmount: response.finalAmount || orderAmount,
          savings: response.discountAmount || 0,
          message: response.message,
          couponType: response.couponType,
          couponValue: response.couponValue,
          // Kupon kısıtlamaları
          lockedProductId: selectedProduct?.id!,
          lockedUserCount: newUserCount
        };
        
        setAppliedCoupon(appliedCouponData);
        
        // Kupon tipine göre mesaj oluştur
        const discountText = response.couponType === 'percentage' 
          ? `%${response.couponValue} indirim`
          : `₺${response.couponValue} indirim`;
        
        showNotification('success', 'Kupon Uygulandı! 🎉', 
          `${response.message} - ${discountText} uygulandı! ₺${Math.round(response.discountAmount || 0)} tasarruf!`);
            
      } else {
        // Kupon geçersiz
        showNotification('error', 'Kupon Hatası', response.message || 'Geçersiz kupon kodu');
      }
      
    } catch (error: any) {
      console.error('Kupon doğrulama hatası:', error);
      const errorMessage = error.response?.data?.message || 'Kupon doğrulanırken bir hata oluştu. Lütfen tekrar deneyin.';
      showNotification('error', 'Bağlantı Hatası', errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  // Kupon kaldırma
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    showNotification('info', 'Kupon Kaldırıldı', 'Kupon indirimi kaldırıldı');
  };

  // Grace period için paket seçimi - kupon kontrolü ile
  const handleProductSelection = (product: Product) => {
    // Kupon uygulanmışsa ve paket değiştiriliyorsa uyarı ver
    if (sub?.status === 'in_grace_period' && appliedCoupon && appliedCoupon.lockedProductId !== product.id) {
      showNotification('warning', 'Kupon İptal Edildi', 'Paketi değiştirdiğiniz için kupon iptal edildi. Kupon kodunuzu tekrar girebilirsiniz.');
      setAppliedCoupon(null);
      setCouponCode('');
    }
    
    setSelectedProduct(product);
  };



  // Grace period için kullanıcı sayısı değişimi - kupon kontrolü ile
  const handleGracePeriodUserCountChange = (value: number | null) => {
    const minValue = activeUsers || 5;

    if (value && value < minValue) {
      message.warning(`Kullanıcı sayısı mevcut aktif kullanıcı sayısının (${minValue}) altında olamaz.`);
      setNewUserCount(minValue);
      return;
    }
    
    const newValue = value || minValue;
    
    // Kupon uygulanmışsa ve kişi sayısı değiştiriliyorsa uyarı ver
    if (sub?.status === 'in_grace_period' && appliedCoupon && appliedCoupon.lockedUserCount !== newValue) {
      showNotification('warning', 'Kupon İptal Edildi', 'Kişi sayısını değiştirdiğiniz için kupon iptal edildi. Kupon kodunuzu tekrar girebilirsiniz.');
      setAppliedCoupon(null);
      setCouponCode('');
    }
    
    setNewUserCount(newValue);
  };



  return (
    <div className="page-wrapper settings-page">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row align-items-center mb-3">
            <div className="col">
              <Title level={3} className="m-0">
                <Space>
                  {t('companySettings.pageTitle')}
                  {/* <Badge count={Object.keys(form.getFieldsValue()).length} color="blue" /> */}
                </Space>
              </Title>
              <div className="breadcrumb-container mt-2">
                <Text className="breadcrumb-text">{t('companySettings.breadcrumb.management')} / </Text>
                <Text className="breadcrumb-text active">{t('companySettings.breadcrumb.companySettings')}</Text>
              </div>
            </div>
          </div>
        </div>

        <Card className="settings-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              workOnHolidays: false,
              workOnWeekends: false,
              includeManagerInReports: false,
              maxDailyWorkHours: true,
              lateTolerance: false,
              lateTolerationMinutes: 5,
              earlyLeave: true,
              includeWeekendsInAnnualLeave: true,
              sendLeaveRequestEmails: false,
              requirePreApprovalForLeave: true,
              annualLeaveLimit: 23,
              shiftMode: 'fixed',
              ipControl: false
            }}
            className="settings-form"
          >
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab} 
              tabPosition={isMobile ? 'top' : 'left'} 
              size="large"
              className="settings-tabs"
            >

              <TabPane 
                tab={
                  <Space>
                    <CalendarOutlined />
                    Şirket Bilgileri
                  </Space>
                } 
                key="company-settings"
              >
                <BasicInfoForm form={form} companyData={companyData} loading={companyLoading} />
                <TaxInfoForm form={form} companyData={companyData} loading={companyLoading} />
                <ExtraInfoForm form={form} companyData={companyData} loading={companyLoading} />
                <CompanyStatsCard
                  activeUsers={activeUsers || 0}
                  userLimit={sub?.userLimit || 0}
                  loading={companyLoading}
                />
              </TabPane>

              <TabPane tab={<Space><MailOutlined /> E-posta Ayarları</Space>} key="email-settings">
              <EmailSettingsForm
                form={form}
                settings={companyData.settings as CompanySettings}
                loading={companyLoading}
              />
            </TabPane>

            <TabPane tab={<Space><BellOutlined />Bildirim Ayarları</Space>} key="notification-settings">
              <NotificationSettingsForm
                form={form} 
                settings={companyData.settings as CompanySettings}
                loading={companyLoading}
              />
            </TabPane>

            <TabPane tab={<Space><MessageOutlined /> SMS Ayarları</Space>} key="sms-settings">
              <SmsSettingsForm
                form={form}
                settings={companyData.settings as CompanySettings}
                loading={companyLoading}
              />
            </TabPane>





              {/* ⚠️ CRITICAL: Payment tab - sadece gerekli durumlarda göster */}
              {shouldShowPaymentTab() && (
              <TabPane
                tab={
                  <Space>
                    <DollarOutlined />
                      {sub?.status === 'trial' ? 'Premium Planlar' : 'Plan Yönetimi'}
                  </Space>
                } 
                key="payment-settings"
              >
                <div className="tab-content">
                  <Title level={4} className="section-title">
                      <DollarOutlined style={{ marginRight: 8 }}/> 
                      {sub?.status === 'trial' ? 'Premium Planlar' : 'Plan Yönetimi'}
                  </Title>
                  <Text type="secondary" className="section-description">
                    {sub?.status === 'trial' 
                        ? `${sub.message} Premium planlarımızı inceleyip kesintisiz hizmet alabilirsiniz.`
                      : 'Kullanıcı limitinizi artırabilir ve plan detaylarınızı yönetebilirsiniz.'
                    }
                  </Text>
                  
                    {/* Trial kullanıcı için Premium Plan Tanıtımı */}
                  {sub?.status === 'trial' && (
                      <div className="settings-section">
                        <Row gutter={[24, 24]} className="mt-4">
                          

                          {/* Mevcut Plan Durumu */}
                          <Col xs={24}>
                            <Alert
                              message={
                                <Space>
                                  <ClockCircleOutlined />
                                  <Text strong>
                                    Deneme Süresi Durumu
                                  </Text>
                                </Space>
                              }
                              description={
                                <div>
                                  <Text>{sub.message}</Text>
                                  <br />
                                  <Text type="secondary">Mevcut kullanıcı limiti: {sub.userLimit} kullanıcı</Text><br/>
                                  <Text type="secondary">Aktif kullanıcı: {activeUsers} kullanıcı</Text>
                                </div>
                              }
                              type="warning"
                              showIcon
                              style={{ marginBottom: 24 }}
                            />
                          </Col>

                          {/* Premium Plan Satın Alma - Product Bazlı Sistem */}
                          <Col xs={24}>
                            <Card className="premium-purchase-card" style={{ border: '1px solid #b7eb8f',padding: '16px',borderRadius: '8px' }}>
                              <Title level={4} className="mb-3">
                                <CreditCardOutlined className="me-2" />
                                Premium Planı Satın Al
                              </Title>
                              
                              {/* Kullanıcı Sayısı Hesaplayıcı */}
                              <Card className="mb-4" size="small" style={{ border: '1px solid #b7eb8f',padding: '16px',borderRadius: '8px' }}>
                                <Row gutter={[24, 16]} align="middle">
                                  <Col xs={24}>
                                    <div className="calculator-section">
                                      <Title level={5} className="calculator-title">
                                        <TeamOutlined className="me-2" />
                                        Kaç kullanıcınız olacak?
                                      </Title>
                                      <InputNumber
                                        min={parseInt(sub?.userLimit?.toString() || '1', 10)}
                                        max={500000}
                                        value={newUserCount}
                                        onChange={(value) => {
                                          const minValue = parseInt(sub?.userLimit?.toString() || '1', 10);
                                          if (value && value < minValue) {
                                            message.warning(`Kullanıcı sayısı mevcut limitin (${minValue}) altında olamaz.`);
                                            setNewUserCount(minValue);
                                            return;
                                          }
                                          
                                          // Trial için kupon kontrolü
                                          if (appliedCoupon && appliedCoupon.lockedUserCount !== (value || minValue)) {
                                            showNotification('warning', 'Kupon İptal Edildi', 'Kişi sayısını değiştirdiğiniz için kupon iptal edildi. Kupon kodunuzu tekrar girebilirsiniz.');
                                            setAppliedCoupon(null);
                                            setCouponCode('');
                                          }
                                          
                                          setNewUserCount(value || minValue);
                                        }}
                                        size="large"
                                        style={{ width: '100%' }}
                                        parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                        addonAfter="kullanıcı"

                                      />
                                      <Text type="secondary" className="pricing-info mt-2">
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                          Minimum: {activeUsers} kullanıcı (mevcut aktif sayı)
                                        </Text>
                                                                                  {appliedCoupon && (
                                           <Text type="warning" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                             ✨ Kişi sayısı değiştirilirse "{appliedCoupon.code}" kupon kodunuz devre dışı olacak. Kuponu tekrar kullanabilirsiniz.
                                           </Text>
                                          )}
                                      </Text>
                                    </div>
                                  </Col>
                                </Row>
                              </Card>

                              {/* Paket Seçimi - Trial için */}
                              <Card className="payment-method-section mb-4" size="small" style={{ border: '1px solid #b7eb8f',padding: '16px',borderRadius: '8px' }}>
                                <Title level={5} className="mb-3">
                                  <DollarOutlined className="me-2" />
                                  Paket Süresini Seçin
                                  {appliedCoupon && (
                                    <Text type="warning" style={{ fontSize: '12px', display: 'block', marginTop: '4px', fontWeight: 'normal' }}>
                                      ✨ Paket değiştirilirse "{appliedCoupon.code}" kupon kodunuz devre dışı olacak. Kuponu tekrar kullanabilirsiniz.
                                    </Text>
                                  )}
                                </Title>

                                <Row gutter={[16, 16]}>
                                  {products.filter(p => p.isActive).map((product) => (
                                    <Col xs={24} sm={12} lg={8} key={product.id}>
                                      <div 
                                        className={`package-selection-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                                        onClick={() => handleProductSelection(product)}
                                        style={{
                                          border: selectedProduct?.id === product.id ? '2px solid rgb(205, 73, 246)' : '1px solid #e2e8f0',
                                          borderRadius: '12px',
                                          padding: '20px',
                                          cursor: 'pointer',
                                          transition: 'all 0.3s ease',
                                          background: selectedProduct?.id === product.id ? 'rgb(245, 240, 254)' : 'white',
                                          position: 'relative',
                                          height: '100%'
                                        }}
                                      >
                                        {/* İndirim yüzdesi sticker'ı - 3 aylık paket dışında */}
                                        {product.durationMonths !== 3 && (() => {
                                          const threeMonthProduct = products.find(p => p.durationMonths === 3);
                                          if (threeMonthProduct) {
                                            const threeMonthPrice = Number(threeMonthProduct.pricePerUserPerMonth);
                                            const currentPrice = Number(product.pricePerUserPerMonth);
                                            const discountPercentage = Math.round(((threeMonthPrice - currentPrice) / threeMonthPrice) * 100);
                                            
                                            if (discountPercentage > 0) {
                                              return (
                                                <div style={{
                                                  position: 'absolute',
                                                  top: '-24px',
                                                  right: '-12px',
                                                  background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                                  color: 'white',
                                                  borderRadius: '50%',
                                                  width: '50px',
                                                  height: '50px',
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontSize: '10px',
                                                  fontWeight: 'bold',
                                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                  zIndex: 10,
                                                  textAlign: 'center',
                                                  lineHeight: '1'
                                                }}>
                                                  <div style={{ fontSize: '18px', marginBottom: '1px' }}>
                                                    %{discountPercentage}
                                                  </div>
                                                  <div style={{ fontSize: '10px' }}>
                                                    İndirim
                                                  </div>
                                                </div>
                                              );
                                            }
                                          }
                                          return null;
                                        })()}

                                        {selectedProduct?.id === product.id && (
                                          <div style={{
                                            position: 'absolute',
                                            top: '12px',
                                            left: '12px',
                                            background: '#1890ff',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            zIndex: 11
                                          }}>
                                            ✓
                                          </div>
                                        )}
                                        
                                        <div style={{ textAlign: 'center' }}>
                                          <Title level={5} style={{ color: '#2d3748', marginBottom: '8px' }}>
                                            {product.name}
                                          </Title>
                                          
                                          <div style={{ marginBottom: '12px' }}>
                                            {/* 3 aylık paket dışında üzeri çizili fiyat göster */}
                                            {product.durationMonths !== 3 && (
                                              <div style={{ marginBottom: '4px' }}>
                                                <Text 
                                                  style={{ 
                                                    fontSize: '16px', 
                                                    fontWeight: 'normal', 
                                                    color: '#8c8c8c',
                                                    textDecoration: 'line-through'
                                                  }}
                                                >
                                                  ₺{(Number(products.find(p => p.durationMonths === 3)?.pricePerUserPerMonth))}
                                                </Text>
                                                <br/>
                                              </div>
                                            )}
                                            
                                            {/* Mevcut fiyat */}
                                            <div>
                                              <Text style={{ fontSize: '24px', fontWeight: 'bold', color: 'rgb(181, 44, 223)' }}>
                                                ₺{product.pricePerUserPerMonth}
                                              </Text>
                                              <br/>
                                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                                /kullanıcı/ay
                                              </Text>
                                            </div>
                                          </div>
                                          
                                          {product.description && (
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                              {product.description}
                                            </Text>
                                          )}
                                        </div>
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              </Card>

                              {/* Kupon Kodu Bölümü - Trial için */}
                              <Card className="coupon-section-card" style={{ border: '1px solid #b7eb8f',padding: '16px',borderRadius: '8px', marginBottom: '16px' }}>
                                <Title level={5} className="mb-3">
                                  <TagOutlined className="me-2" />
                                  Kupon Kodunuz Var mı?
                                </Title>
                                
                                <Row gutter={[16, 16]} align="middle">
                                  <Col xs={24}>
                                    <Space.Compact style={{ width: '100%' }}>
                                      <Input
                                        placeholder="Kupon kodunu girin"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                        size="large"
                                        disabled={!!appliedCoupon || !selectedProduct}
                                        style={{
                                          borderRadius: '12px 0 0 12px',
                                          border: '2px solid #e2e8f0',
                                          fontSize: '16px',
                                          height: '48px'
                                        }}
                                      />
                                      <Button
                                        type="primary"
                                        onClick={handleApplyCoupon}
                                        loading={couponLoading}
                                        disabled={!!appliedCoupon || !selectedProduct}
                                        size="large"
                                        style={{
                                          height: '48px',
                                          borderRadius: '0 12px 12px 0',
                                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                          border: 'none',
                                          fontWeight: 600
                                        }}
                                      >
                                        Uygula
                                      </Button>
                                    </Space.Compact>
                                  </Col>
                                </Row>

                                {!selectedProduct && (
                                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                    Kupon kodu kullanmak için önce bir paket seçin
                                  </Text>
                                )}

                                {appliedCoupon && (
                                  <Alert
                                    message={
                                      <Space>
                                        <PercentageOutlined />
                                        <span>Kupon Uygulandı: <strong>{appliedCoupon.code}</strong></span>
                                        {appliedCoupon.couponType && (
                                          <Badge 
                                            color={appliedCoupon.couponType === 'percentage' ? 'green' : 'blue'}
                                            text={appliedCoupon.couponType === 'percentage' 
                                              ? `%${appliedCoupon.couponValue} İndirim`
                                              : `₺${appliedCoupon.couponValue} İndirim`
                                            }
                                          />
                                        )}
                                      </Space>
                                    }
                                    description={
                                      <div>
                                        <Text>{appliedCoupon.message}</Text>
                                        <br />
                                        <Text style={{ color: '#11998e', fontWeight: 600 }}>
                                          <strong>Tasarruf: ₺{Math.round(appliedCoupon.savings * 100) / 100}</strong>
                                        </Text>
                                      </div>
                                    }
                                    type="success"
                                    showIcon
                                    closable
                                    onClose={handleRemoveCoupon}
                                    style={{
                                      marginTop: '16px',
                                      borderRadius: '12px',
                                      border: '2px solid #11998e'
                                    }}
                                  />
                                )}
                              </Card>
                              {/* Fiyat Özeti - Sadece paket seçildiyse */}
                              {selectedProduct && (
                                <Card className="price-breakdown-card mb-4" size="small" style={{ border: '1px solid #b7eb8f',padding: '16px',borderRadius: '8px' }}>
                                  <Title level={5} className="mb-3">
                                    <CalculatorOutlined className="me-2" />
                                    Fiyat Detayları
                                  </Title>
                                  
                                  <div className="price-breakdown-details">
                                    <Row justify="space-between" className="price-line">
                                      <Col><Text>Paket Ücreti ({newUserCount} kullanıcı × {selectedProduct.durationMonths} ay)</Text></Col>
                                      <Col><Text>₺{(newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth).toFixed(2)}</Text></Col>
                                    </Row>

                                    {appliedCoupon && (
                                      <Row justify="space-between" className="price-line discount">
                                        <Col><Text type="success">Kupon İndirimi ({appliedCoupon.code})</Text></Col>
                                        <Col><Text type="success">-₺{appliedCoupon.savings.toFixed(2)}</Text></Col>
                                      </Row>
                                    )}

                                    <Divider style={{ margin: '12px 0' }} />

                                    <Row justify="space-between" className="price-line subtotal">
                                      <Col><Text strong>Ara Toplam</Text></Col>
                                      <Col><Text strong>₺{(appliedCoupon ? appliedCoupon.finalAmount : newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth).toFixed(2)}</Text></Col>
                                    </Row>

                                    <Row justify="space-between" className="price-line tax">
                                      <Col>
                                        <Text>
                                          KDV (%20)
                                          <Tooltip title="Katma Değer Vergisi yasal olarak eklenir">
                                            <InfoCircleOutlined className="ms-1" />
                                          </Tooltip>
                                        </Text>
                                      </Col>
                                      <Col><Text>₺{((appliedCoupon ? appliedCoupon.finalAmount : newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth) * 0.20).toFixed(2)}</Text></Col>
                                    </Row>

                                    <Divider style={{ margin: '12px 0' }} />

                                    <Row justify="space-between" className="price-line total">
                                      <Col><Title level={5} style={{ margin: 0 }}>Toplam Tutar</Title></Col>
                                      <Col>
                                        <Title level={4} style={{ margin: 0, color: 'rgb(181, 44, 223)' }}>
                                          ₺{((appliedCoupon ? appliedCoupon.finalAmount : newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth) * 1.20).toFixed(2)}
                                          <Text type="secondary" className="ms-1">
                                            /{selectedProduct.durationMonths} ay
                                          </Text>
                                        </Title>
                                      </Col>
                                    </Row>
                                  </div>
                                </Card>
                              )}

                              
                              {/* Billing Address Form - Trial için */}
                              <BillingAddressForm
                                form={null}
                                companyData={companyData}
                                onBillingAddressChange={setBillingAddress}
                              />

                              

                              {/* Ödeme Formu - Trial için */}
                              <Card className="payment-form-card" style={{ border: '1px solid #b7eb8f',padding: '16px',borderRadius: '8px' }}>
                                <Title level={5} className="mb-3">
                                  <CreditCardOutlined className="me-2" />
                                  Ödeme Bilgileri
                                </Title>
                                
                                <Alert
                                  message="Güvenli Ödeme Garantisi"
                                  description="Tüm ödeme bilgileriniz 256-bit SSL şifreleme ile korunur. Kart bilgileriniz saklanmaz."
                                  type="info"
                                  showIcon
                                  className="mb-4"
                                />

                                <div className="purchase-section">
                                  <Alert
                                    message="Güvenli Ödeme"
                                    description={(() => {
                                      if (!selectedProduct) return "Paket seçimi yapın";
                                      const basePrice = newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
                                      const finalPrice = appliedCoupon 
                                        ? appliedCoupon.finalAmount * (1 + 0.20)
                                        : basePrice * (1 + 0.20);
                                      const couponText = appliedCoupon ? ` (${appliedCoupon.code} kuponu uygulandı)` : '';
                                      return `Güvenli ödeme sayfasına yönlendirileceksiniz. ${selectedProduct.durationMonths} aylık plan için ₺${Math.round(finalPrice)} (KDV dahil) tahsil edilecektir${couponText}. Tüm ödeme bilgileriniz SSL ile korunur.`;
                                    })()}
                                    type="info"
                                    showIcon
                                    className="mb-4"
                                  />

                                  <div className="text-center">
                                    <Button
                                      type="primary"
                                      icon={<CreditCardOutlined />}
                                      loading={saving}
                                      size="large"
                                      disabled={!selectedProduct || !billingAddress}
                                      onClick={async () => {
                                        try {
                                          setSaving(true);
                                          
                                          if (!selectedProduct) {
                                            showNotification('warning', 'Uyarı', 'Lütfen bir paket seçin.');
                                            setSaving(false);
                                            return;
                                          }
                                          
                                          const baseAmount = newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
                                          const finalAmountBeforeTax = appliedCoupon ? appliedCoupon.finalAmount : baseAmount;
                                          const finalAmountWithTax = finalAmountBeforeTax * 1.20;
                                          
                                          const checkoutData = {
                                            plan: selectedProduct.durationMonths === 12 ? 'yearly' as const : 'monthly' as const,
                                            userLimit: newUserCount,
                                            companyId: user?.companyId!
                                          };

                                          const result = await createCheckoutSession(checkoutData);
                                          
                                          if (result.checkoutForm && result.checkoutForm.checkoutFormContent) {
                                            localStorage.setItem('checkoutsession', result.checkoutForm.token);
                                            localStorage.setItem('conversationId', result.checkoutForm.conversationId);
                                            
                                            const newWindow = window.open('', '_blank');
                                            if (newWindow) {
                                              newWindow.document.write(result.checkoutForm.checkoutFormContent);
                                              newWindow.document.close();
                                              showNotification('success', '💳 Ödeme Sayfası Açıldı!', 'Ödeme işleminizi yeni sekmede tamamlayın. Başarılı ödeme sonrası sayfa otomatik güncellenecek.');
                                            } else {
                                              showNotification('error', 'Popup Engellendi', 'Ödeme sayfasını açmak için lütfen tarayıcınızda popup\'lara izin verin ve tekrar deneyin.');
                                            }
                                          }
                                          
                                        } catch (error) {
                                          console.error('Checkout error:', error);
                                          message.error('Ödeme formu yüklenirken bir hata oluştu');
                                        } finally {
                                          setSaving(false);
                                        }
                                      }}
                                      style={{ 
                                        height: '60px', 
                                        fontSize: '18px',
                                        minWidth: '300px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: 'none'
                                      }}
                                    >
                                      {saving 
                                        ? 'Ödeme Sayfası Hazırlanıyor...'
                                        : (() => {
                                            if (!selectedProduct) return 'Paket Seçin - Ödeme Yap';
                                            const basePrice = newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
                                            const finalPrice = appliedCoupon 
                                              ? appliedCoupon.finalAmount * (1 + 0.20)
                                              : basePrice * (1 + 0.20);
                                            return `₺${Math.round(finalPrice)} - Güvenli Ödeme Sayfasına Git`;
                                          })()
                                      }
                                    </Button>
                                  </div>
                                </div>
                              </Card>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                  )}

                  {/* Premium kullanıcı için mevcut plan bilgileri */}
                  {sub?.status === 'active' && (
                    <Alert
                      message="Mevcut Plan Durumu"
                      description={
                        <div>
                          <Text strong>Mevcut kullanıcı limiti: {sub.userLimit} kullanıcı</Text><br/>
                          <Text strong>Aktif kullanıcı sayısı: {activeUsers} kullanıcı</Text><br/>
                          <Text type="secondary">
                            {sub.duration} aylık plan aktif 
                            {/* - {sub.daysLeft} gün kaldı */}
                          </Text><br/>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {sub.message}
                          </Text>
                        </div>
                      }
                      type="success"
                      showIcon
                      style={{ marginBottom: 24 }}
                    />
                  )}
                  
                  {/* Premium kullanıcı için yönetim paneli */}
                  {sub?.status === 'active' && (
                    <div className="settings-section">
                      <Row gutter={[24, 24]} className="mt-4">
                        {/* Kullanıcı Sayısı Düzenleme */}
                        <Col xs={24}>
                          <Card className="setting-card" title={<><TeamOutlined className="me-2" />Kullanıcı Limiti Yönetimi</>}>
                            <Row gutter={[16, 16]}>
                              <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                  label={
                                    <Space>
                                      Eklenecek Kullanıcı Sayısı
                                      {renderTooltip("Mevcut kullanıcı limitinize ek olarak kaç kullanıcı eklemek istediğinizi belirleyin.")}
                                    </Space>
                                  }
                                >
                                  <InputNumber 
                                    min={0} 
                                    max={500000 - currentUserCount}
                                    value={additionalUserCount}
                                    onChange={handleAdditionalUserCountChange}
                                    style={{ width: '100%' }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                    addonAfter="ek kullanıcı"
                                  />
                                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                    Mevcut limit: {currentUserCount} kullanıcı → Yeni limit: {currentUserCount + additionalUserCount} kullanıcı
                                  </Text>
                                  {/* <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '2px', fontStyle: 'italic', color: '#1890ff' }}>
                                    💡 Her ek kullanıcı için ₺{calculatePricing(1).monthlyUserPrice || 0}/ay × kalan süre ({calculatePricing(1).remainingMonths} ay{calculatePricing(1).remainingDays > 0 ? ` + ${calculatePricing(1).remainingDays} gün` : ''})
                                  </Text> */}
                                </Form.Item>
                              </Col>

                            {/* Anlık Fiyat Hesaplaması ve Ödeme Formu */}
                            {showPriceCalculation && additionalUserCount > 0 && (
                              <Col xs={24}>
                                <Card size="small" className="price-calculation-card" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                  <Title level={5} style={{ color: '#389e0d', marginBottom: 12 }}>
                                    <GiftOutlined className="me-2" />
                                    Plan Güncelleme - Ödeme Bilgileri
                                  </Title>
                                  
                                  {/* Fiyat Özeti */}
                                  <Row gutter={[16, 12]} style={{ marginBottom: 24 }}>
                                    <Col xs={24} sm={12} md={appliedCoupon ? 6 : 8}>
                                      <Statistic
                                        title="Ek Kullanıcı Ücreti (Kalan Süre)"
                                        value={calculatePricing(additionalUserCount).proportionalPrice}
                                        precision={2}
                                        suffix="₺"
                                        valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                                      />
                                      {/* <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {additionalUserCount} ek kullanıcı × kalan süre ({calculatePricing(additionalUserCount).remainingMonths} ay{calculatePricing(additionalUserCount).remainingDays > 0 ? ` + ${calculatePricing(additionalUserCount).remainingDays} gün` : ''})
                                      </Text> */}
                                    </Col>
                                    
                                    {appliedCoupon && (
                                      <Col xs={24} sm={12} md={6}>
                                        <Statistic
                                          title="Kupon İndirimi"
                                          value={appliedCoupon.savings}
                                          precision={2}
                                          suffix="₺"
                                          valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                                        />
                                        <Text type="success" style={{ fontSize: '12px' }}>
                                          {appliedCoupon.code} kuponu
                                        </Text>
                                      </Col>
                                    )}
                                    
                                    <Col xs={24} sm={12} md={appliedCoupon ? 6 : 8}>
                                      <Statistic
                                        title="KDV Dahil Tutar"
                                        value={appliedCoupon 
                                          ? appliedCoupon.finalAmount * (1 + 0.20)
                                          : calculatePricing(additionalUserCount).proportionalPriceWithTax
                                        }
                                        precision={2}
                                        suffix="₺"
                                        valueStyle={{ color: '#d48806', fontSize: '18px' }}
                                      />
                                      <Text type="secondary" style={{ fontSize: '12px' }}>
                                        %{0.20 * 100} KDV dahil (kalan süre için)
                                      </Text>
                                    </Col>
                                    
                                    <Col xs={24} sm={12} md={appliedCoupon ? 6 : 8}>
                                      <Statistic
                                        title="Yeni Toplam Limit"
                                        value={currentUserCount + additionalUserCount}
                                        suffix="kullanıcı"
                                        valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                                      />
                                      <Text type="success" style={{ fontSize: '12px' }}>
                                        {currentUserCount} + {additionalUserCount} ek
                                      </Text>
                                    </Col>
                                  </Row>

                                  <Divider />

                                  {/* Kupon Kodu Bölümü - Premium için */}
                                  <Card className="coupon-section-card" style={{ border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                                    <Title level={5} style={{ marginBottom: 16 }}>
                                      <TagOutlined className="me-2" />
                                      Kupon Kodunuz Var mı?
                                    </Title>
                                    
                                    <Row gutter={[16, 16]} align="middle">
                                      <Col xs={24}>
                                        <Space.Compact style={{ width: '100%' }}>
                                          <Input
                                            placeholder="Kupon kodunu girin"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                            size="large"
                                            disabled={!!appliedCoupon || additionalUserCount === 0}
                                            style={{
                                              borderRadius: '12px 0 0 12px',
                                              border: '2px solid #e2e8f0',
                                              fontSize: '16px',
                                              height: '48px'
                                            }}
                                          />
                                          <Button
                                            type="primary"
                                            onClick={handleApplyCoupon}
                                            loading={couponLoading}
                                            disabled={!!appliedCoupon || additionalUserCount === 0}
                                            size="large"
                                            style={{
                                              height: '48px',
                                              borderRadius: '0 12px 12px 0',
                                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                              border: 'none',
                                              fontWeight: 600
                                            }}
                                          >
                                            Uygula
                                          </Button>
                                        </Space.Compact>
                                      </Col>
                                    </Row>

                                    {additionalUserCount === 0 && (
                                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                        Kupon kodu kullanmak için önce ek kullanıcı sayısını belirleyin
                                      </Text>
                                    )}
                                    
                                    {appliedCoupon && additionalUserCount > 0 && (
                                      <Text type="warning" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                        ✨ Kupon otomatik olarak yeni kullanıcı sayısına uygulanacak
                                      </Text>
                                    )}

                                    {appliedCoupon && (
                                      <Alert
                                        message={
                                          <Space>
                                            <PercentageOutlined />
                                            <span>Kupon Uygulandı: <strong>{appliedCoupon.code}</strong></span>
                                            {appliedCoupon.couponType && (
                                              <Badge 
                                                color={appliedCoupon.couponType === 'percentage' ? 'green' : 'blue'}
                                                text={appliedCoupon.couponType === 'percentage' 
                                                  ? `%${appliedCoupon.couponValue} İndirim`
                                                  : `₺${appliedCoupon.couponValue} İndirim`
                                                }
                                              />
                                            )}
                                          </Space>
                                        }
                                        description={
                                          <div>
                                            <Text>{appliedCoupon.message}</Text>
                                            <br />
                                            <Text style={{ color: '#11998e', fontWeight: 600 }}>
                                              <strong>Tasarruf: ₺{Math.round(appliedCoupon.savings * 100) / 100}</strong>
                                            </Text>
                                          </div>
                                        }
                                        type="success"
                                        showIcon
                                        closable
                                        onClose={handleRemoveCoupon}
                                        style={{
                                          marginTop: '16px',
                                          borderRadius: '12px',
                                          border: '2px solid #11998e'
                                        }}
                                      />
                                    )}
                                  </Card>
                                  <BillingAddressForm
                                    form={null}
                                    companyData={companyData}
                                    onBillingAddressChange={setBillingAddress}
                                  />
                                  {/* Ödeme Formu */}
                                  <Title level={5} style={{ marginBottom: 16 }}>
                                    <CreditCardOutlined className="me-2" />
                                    Ödeme Bilgileri
                                  </Title>
                                  <div className="upgrade-purchase-section">
                                    <Alert
                                      message="Plan Yükseltme"
                                      description="Güvenli ödeme sayfasına yönlendirileceksiniz. Sadece artış tutarı tahsil edilecektir."
                                      type="info"
                                      showIcon
                                      className="mb-4"
                                    />

                                    <div style={{ marginTop: '16px', textAlign: 'right' }}>
                                      <Space>
                                        <Button 
                                          onClick={() => {
                                            setAdditionalUserCount(0);
                                            setShowPriceCalculation(false);
                                          }}
                                          disabled={saving}
                                        >
                                          İptal
                                        </Button>
                                        <Button
                                          type="primary"
                                          icon={<CreditCardOutlined />}
                                          loading={saving}
                                          disabled={additionalUserCount === 0}
                                          onClick={async () => {
                                            try {
                                              setSaving(true);
                                              
                                              const additionalUserPricing = calculatePricing(additionalUserCount);
                                              const finalAmount = appliedCoupon 
                                                ? appliedCoupon.finalAmount * 1.20 // KDV'yi kuponlu fiyata ekle
                                                : additionalUserPricing.proportionalPriceWithTax;

                                              const addUsersData = {
                                                additionalUsers: additionalUserCount,
                                                finalAmount: finalAmount,
                                                billingAddress: billingAddress || {
                                                  alias: "Varsayılan Adres",
                                                  address: companyData?.address || "Varsayılan Adres",
                                                  city: companyData?.city || "İstanbul", 
                                                  zipCode: companyData?.zipCode || "34000",
                                                  country: companyData?.country || "Turkey",
                                                  contactName: `${user?.firstName || ''} ${user?.lastName || ''}`
                                                },
                                              };

                                              const result = await addUsers(addUsersData);
                                              
                                              // IYZICO checkout form'unu yeni pencerede aç
                                              if (result.checkoutForm && result.checkoutForm.checkoutFormContent) {
                                                // Token'ları localStorage'a kaydet (callback için)
                                                localStorage.setItem('checkoutsession', result.checkoutForm.token);
                                                localStorage.setItem('conversationId', result.checkoutForm.conversationId);
                                                
                                                // Yeni pencerede checkout form'u aç
                                                const newWindow = window.open('', '_blank');
                                                if (newWindow) {
                                                  newWindow.document.write(result.checkoutForm.checkoutFormContent);
                                                  newWindow.document.close();
                                                  showNotification('success', '💳 Ödeme Sayfası Açıldı!', 'Ödeme işleminizi yeni sekmede tamamlayın. Başarılı ödeme sonrası sayfa otomatik güncellenecek.');
                                                } else {
                                                  showNotification('error', 'Popup Engellendi', 'Ödeme sayfasını açmak için lütfen tarayıcınızda popup\'lara izin verin ve tekrar deneyin.');
                                                }
                                              }
                                              
                                              // Form işlemlerini temizle
                                              setAdditionalUserCount(0);
                                              setAppliedCoupon(null);
                                              setCouponCode('');
                                              setShowPriceCalculation(false);
                                              
                                            } catch (error) {
                                              console.error('Add users error:', error);
                                            } finally {
                                              setSaving(false);
                                            }
                                          }}
                                          style={{ minWidth: 180 }}
                                        >
                                          {saving 
                                            ? 'Ödeme Sayfası Hazırlanıyor...' 
                                            : (() => {
                                                const additionalUserPricing = calculatePricing(additionalUserCount);
                                                const finalPrice = appliedCoupon 
                                                  ? appliedCoupon.finalAmount * (1 + 0.20)
                                                  : additionalUserPricing.proportionalPriceWithTax;
                                                return `₺${finalPrice.toFixed(2)} - Güvenli Ödeme`;
                                              })()
                                          }
                                        </Button>
                                      </Space>
                                    </div>
                                  </div>
                                </Card>
                              </Col>
                            )}
                          </Row>
                        </Card>
                      </Col>
                    </Row>
                    </div>
                  )}
                </div>
              </TabPane>
              )}

              {/* Grace Period - Acil Ödeme Gerekli Tab */}
              {sub?.status === 'in_grace_period' && (
                <TabPane 
                  tab={
                    <Space>
                      <ExclamationCircleOutlined style={{ color: 'rgb(181, 44, 223)' }} />
                      <span style={{ color: 'rgb(181, 44, 223)', fontWeight: 'bold' }}>
                        Ödeme Gerekli
                      </span>
                    </Space>
                  } 
                  key="grace-period-payment"
                >
                  <div className="tab-content">
                    <div className="grace-period-header" style={{ marginBottom: '24px' }}>
                      <Alert
                        message="Abonelik Süresi Doldu - Ödeme Gerekli"
                        description={
                          <div>
                            <Text>
                              Abonelik süreniz dolmuş ve şu anda <strong>(ek süre)</strong> içindesiniz. 
                              Bu süre zarfında uygulamayı kullanmaya devam edebilirsiniz ancak <strong style={{ color: '#faad14' }}>
                              {sub.daysLeft} gün</strong> içinde ödeme yapmanız gerekmektedir.
                            </Text>
                            <br />
                            <Text type="secondary">
                              Ödeme yapmazsanız hesabınız askıya alınacaktır.
                            </Text>
                          </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: '24px' }}
                      />
                    </div>

                    <Title level={4} className="section-title">
                      <CreditCardOutlined style={{ marginRight: 8, color: '#faad14' }}/> 
                      Yeni Abonelik Satın Al
                    </Title>
                    <Text type="secondary" className="section-description">
                      Mevcut aktif kullanıcı sayınız baz alınarak yeni abonelik planınızı seçin ve ödeme yapın.
                    </Text>
                    
                    <div className="settings-section">
                      <Row gutter={[24, 24]} className="mt-4">
                        <Col xs={24}>
                          <Card className="grace-period-purchase-card" style={{padding: '16px'}}>
                            <Title level={4} className="mb-3">
                              <CreditCardOutlined className="me-2" />
                              Yeni Abonelik Planı
                            </Title>
                            
                            {/* Kullanıcı Sayısı Hesaplayıcı */}
                            <Card className="mb-4" size="small" style={{padding: '16px'}}>
                              <Row gutter={[24, 16]} align="middle">
                                <Col xs={24}>
                                  <div className="calculator-section">
                                    <Title level={5} className="calculator-title">
                                      <TeamOutlined className="me-2" />
                                      Kaç kullanıcınız olacak?
                                    </Title>
                                    <InputNumber
                                      min={activeUsers}
                                      max={500000}
                                      value={newUserCount}
                                      onChange={handleGracePeriodUserCountChange}
                                      size="large"
                                      style={{ width: '100%' }}

                                      parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                      addonAfter="kullanıcı"
                                    />
                                    <Text type="secondary" className="pricing-info mt-2">
                                      {/* Her kullanıcı: <strong>₺{calculatePricing(1).monthlyUserPrice || 0}/ay</strong>
                                      <br /> */}
                                      <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Minimum: {activeUsers} kullanıcı (mevcut aktif sayı)
                                      </Text>
                                      {appliedCoupon && (
                                        <Text type="warning" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                          ✨ Kişi sayısı değiştirilirse "{appliedCoupon.code}" kupon kodunuz devre dışı olacak. Kuponu tekrar kullanabilirsiniz.
                                        </Text>
                                      )}
                                    </Text>
                                  </div>
                                </Col>
                              </Row>
                            </Card>
                            
                            
                            {/* Paket Seçimi - Gerçek Product'lar */}
                            <Card className="payment-method-section mb-4" size="small" style={{padding: '16px'}}>
                              <Title level={5} className="mb-3">
                                <DollarOutlined className="me-2" />
                                Paket Süresini Seçin
                                {appliedCoupon && (
                                  <Text type="warning" style={{ fontSize: '12px', display: 'block', marginTop: '4px', fontWeight: 'normal' }}>
                                    ✨ Paket değiştirilirse "{appliedCoupon.code}" kupon kodunuz devre dışı olacak. Kuponu tekrar kullanabilirsiniz.
                                  </Text>
                                )}
                              </Title>

                              <Row gutter={[16, 16]}>
                                {products.filter(p => p.isActive).map((product) => (
                                  <Col xs={24} sm={12} lg={8} key={product.id}>
                                    <div 
                                      className={`package-selection-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                                      onClick={() => handleProductSelection(product)}
                                      style={{
                                        border: selectedProduct?.id === product.id ? '2px solid rgb(205, 73, 246)' : '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        background: selectedProduct?.id === product.id ? 'rgb(245, 240, 254)' : 'white',
                                        position: 'relative',
                                        height: '100%'
                                      }}
                                    >
                                      {/* İndirim yüzdesi sticker'ı - 3 aylık paket dışında */}
                                      {product.durationMonths !== 3 && (() => {
                                        const threeMonthProduct = products.find(p => p.durationMonths === 3);
                                        if (threeMonthProduct) {
                                          const threeMonthPrice = Number(threeMonthProduct.pricePerUserPerMonth);
                                          const currentPrice = Number(product.pricePerUserPerMonth);
                                          const discountPercentage = Math.round(((threeMonthPrice - currentPrice) / threeMonthPrice) * 100);
                                          
                                          if (discountPercentage > 0) {
                                            return (
                                              <div style={{
                                                position: 'absolute',
                                                top: '-24px',
                                                right: '-12px',
                                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '50px',
                                                height: '50px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                zIndex: 10,
                                                textAlign: 'center',
                                                lineHeight: '1'
                                              }}>
                                                <div style={{ fontSize: '18px', marginBottom: '1px' }}>
                                                  %{discountPercentage}
                                                </div>
                                                <div style={{ fontSize: '10px' }}>
                                                  İndirim
                                                </div>
                                              </div>
                                            );
                                          }
                                        }
                                        return null;
                                      })()}

                                      {selectedProduct?.id === product.id && (
                                        <div style={{
                                          position: 'absolute',
                                          top: '12px',
                                          left: '12px',
                                          background: 'rgb(181, 44, 223)',
                                          color: 'white',
                                          borderRadius: '50%',
                                          width: '20px',
                                          height: '20px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '12px',
                                          zIndex: 11
                                        }}>
                                          ✓
                                        </div>
                                      )}
                                      
                                      <div style={{ textAlign: 'center' }}>
                                        <Title level={5} style={{ color: '#2d3748', marginBottom: '8px' }}>
                                          {product.name}
                                        </Title>
                                        
                                        <div style={{ marginBottom: '12px' }}>
                                          {/* 3 aylık paket dışında üzeri çizili fiyat göster */}
                                          {product.durationMonths !== 3 && (
                                            <div style={{ marginBottom: '4px' }}>
                                              <Text 
                                                style={{ 
                                                  fontSize: '16px', 
                                                  fontWeight: 'normal', 
                                                  color: '#8c8c8c',
                                                  textDecoration: 'line-through'
                                                }}
                                              >
                                                ₺{(Number(products.find(p => p.durationMonths === 3)?.pricePerUserPerMonth))}
                                              </Text>
                                              <br/>
                                            </div>
                                          )}
                                          
                                          {/* Mevcut fiyat */}
                                          <div>
                                            <Text style={{ fontSize: '24px', fontWeight: 'bold', color: 'rgb(181, 44, 223)' }}>
                                              ₺{product.pricePerUserPerMonth}
                                            </Text>
                                            <br/>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                              /kullanıcı/ay
                                            </Text>
                                          </div>
                                        </div>
                                        
                                        {product.description && (
                                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                            {product.description}
                                          </Text>
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            </Card>
                            
                            <Card className="coupon-section-card" style={{ marginBottom: '16px', padding: '16px' }}>
                              <Title level={5} className="mb-3">
                                <TagOutlined className="me-2" />
                                Kupon Kodunuz Var mı?
                              </Title>
                              
                              <Row gutter={[16, 16]} align="middle">
                                <Col xs={24}>
                                  <Space.Compact style={{ width: '100%' }}>
                                    <Input
                                      placeholder="Kupon kodunu girin"
                                      value={couponCode}
                                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                      style={{ flex: 1 }}
                                      disabled={couponLoading || appliedCoupon || !selectedProduct}
                                    />
                                    <Button 
                                      type="primary"
                                      loading={couponLoading}
                                      disabled={!couponCode.trim() || appliedCoupon || !selectedProduct}
                                      onClick={handleApplyCoupon}
                                      style={{ minWidth: '100px' }}
                                    >
                                      {couponLoading ? 'Kontrol Ediliyor...' : 'Uygula'}
                                    </Button>
                                  </Space.Compact>
                                </Col>
                              </Row>

                              {!selectedProduct && (
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                  Kupon kodu kullanmak için önce bir paket seçin
                                </Text>
                              )}

                              {/* Kupon Başarılı Mesajı */}
                              {appliedCoupon && (
                                <Alert
                                  message="Kupon Başarıyla Uygulandı!"
                                  description={
                                    <div>
                                      <Text strong>Kupon Kodu: {appliedCoupon.code}</Text>
                                      <br />
                                      <Text>Kupon Tipi: {appliedCoupon.couponType === 'percentage' ? `%${appliedCoupon.couponValue} İndirim` : `₺${appliedCoupon.couponValue} İndirim`}</Text>
                                      <br />
                                      <Text>Toplam İndirim: ₺{appliedCoupon.savings.toFixed(2)}</Text>
                                      <br />
                                      <Text strong style={{ color: '#52c41a' }}>
                                        Yeni Tutar: ₺{(appliedCoupon.finalAmount * (1 + 0.20)).toFixed(2)} (KDV dahil)
                                      </Text>
                                    </div>
                                  }
                                  type="success"
                                  showIcon
                                  closable
                                  onClose={handleRemoveCoupon}
                                  style={{
                                    marginTop: '16px',
                                    borderRadius: '12px',
                                    border: '2px solid #52c41a'
                                  }}
                                />
                              )}
                            </Card>

                            {/* Fiyat Özeti - Sadece paket seçildiyse */}
                            {selectedProduct && (
                              <Card className="price-breakdown-card mb-4" size="small" style={{padding: '16px'}}>
                                <Title level={5} className="mb-3">
                                  <CalculatorOutlined className="me-2" />
                                  Fiyat Detayları
                                </Title>
                                
                                <div className="price-breakdown-details">
                                  <Row justify="space-between" className="price-line">
                                    <Col><Text>Paket Ücreti ({newUserCount} kullanıcı × {selectedProduct.durationMonths} ay)</Text></Col>
                                    <Col><Text>₺{(newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth).toFixed(2)}</Text></Col>
                                  </Row>

                                  {appliedCoupon && (
                                    <Row justify="space-between" className="price-line discount">
                                      <Col><Text type="success">Kupon İndirimi ({appliedCoupon.code})</Text></Col>
                                      <Col><Text type="success">-₺{appliedCoupon.savings.toFixed(2)}</Text></Col>
                                    </Row>
                                  )}

                                  <Divider style={{ margin: '12px 0' }} />

                                  <Row justify="space-between" className="price-line subtotal">
                                    <Col><Text strong>Ara Toplam</Text></Col>
                                    <Col><Text strong>₺{(appliedCoupon ? appliedCoupon.finalAmount : newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth).toFixed(2)}</Text></Col>
                                  </Row>

                                  <Row justify="space-between" className="price-line tax">
                                    <Col>
                                      <Text>
                                        KDV (%20)
                                        <Tooltip title="Katma Değer Vergisi yasal olarak eklenir">
                                          <InfoCircleOutlined className="ms-1" />
                                        </Tooltip>
                                      </Text>
                                    </Col>
                                    <Col><Text>₺{((appliedCoupon ? appliedCoupon.finalAmount : newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth) * 0.20).toFixed(2)}</Text></Col>
                                  </Row>

                                  <Divider style={{ margin: '12px 0' }} />

                                  <Row justify="space-between" className="price-line total">
                                    <Col><Title level={5} style={{ margin: 0 }}>Toplam Tutar</Title></Col>
                                    <Col>
                                      <Title level={4} style={{ margin: 0, color: 'rgb(181, 44, 223)' }}>
                                        ₺{((appliedCoupon ? appliedCoupon.finalAmount : newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth) * 1.20).toFixed(2)}
                                        <Text type="secondary" className="ms-1">
                                          /{selectedProduct.durationMonths} ay
                                        </Text>
                                      </Title>
                                    </Col>
                                  </Row>
                                </div>
                              </Card>
                            )}

                            {/* Billing Address Form - Grace Period için */}
                            <BillingAddressForm
                              form={null}
                              companyData={companyData}
                              onBillingAddressChange={setBillingAddress}
                            />

                            {/* Acil Ödeme Butonu */}
                            <div className="grace-period-payment-section">
                              <div className="text-center">
                                <Button
                                  type="primary"
                                  icon={<CreditCardOutlined />}
                                  loading={saving}
                                  disabled={!selectedProduct || !billingAddress}
                                  size="large"
                                  onClick={async () => {
                                    try {
                                      setSaving(true);
                                      
                                      if (!selectedProduct) {
                                        showNotification('warning', 'Uyarı', 'Lütfen bir paket seçin.');
                                        setSaving(false);
                                        return;
                                      }
                                      
                                      // Backend interface'ine uygun request body formatı
                                      const baseAmount = newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
                                      const finalAmountBeforeTax = appliedCoupon ? appliedCoupon.finalAmount : baseAmount;
                                      const finalAmountWithTax = finalAmountBeforeTax * 1.20; // KDV dahil tutar
                                      
                                      const purchaseData = {
                                        companyId: user?.companyId!,
                                        userLimit: newUserCount,
                                        duration: selectedProduct.durationMonths,
                                        finalAmount: finalAmountWithTax, // KDV dahil tutar backend'e gönderiliyor
                                        billingAddress: billingAddress || {
                                          alias: "Varsayılan Adres",
                                          address: companyData?.address || "Varsayılan Adres",
                                          city: companyData?.city || "İstanbul", 
                                          zipCode: companyData?.zipCode || "34000",
                                          country: companyData?.country || "Turkey",
                                          contactName: `${user?.firstName || ''} ${user?.lastName || ''}`
                                        },
                                        isInternalRequest: true,
                                        couponCode: appliedCoupon?.code || undefined
                                      };

                                      const result = await subService.purchasePackage(purchaseData);
                                      
                                      // IYZICO checkout form'unu yeni pencerede aç
                                      if (result.checkoutForm && result.checkoutForm.checkoutFormContent) {
                                        // Token'ları localStorage'a kaydet (callback için)
                                        localStorage.setItem('checkoutsession', result.checkoutForm.token);
                                        localStorage.setItem('conversationId', result.checkoutForm.conversationId);
                                        
                                        // Yeni pencerede checkout form'u aç
                                        const newWindow = window.open('', '_blank');
                                        if (newWindow) {
                                          newWindow.document.write(result.checkoutForm.checkoutFormContent);
                                          newWindow.document.close();
                                          showNotification('success', '💳 Ödeme Sayfası Açıldı!', 'Ödeme işleminizi yeni sekmede tamamlayın. Başarılı ödeme sonrası hesabınız yeniden aktif olacak.');
                                        } else {
                                          showNotification('error', 'Popup Engellendi', 'Ödeme sayfasını açmak için lütfen tarayıcınızda popup\'lara izin verin ve tekrar deneyin.');
                                        }
                                      }
                                      
                                    } catch (error) {
                                      console.error('Grace period checkout error:', error);
                                      message.error('Ödeme formu yüklenirken bir hata oluştu');
                                    } finally {
                                      setSaving(false);
                                    }
                                  }}
                                  style={{ 
                                    height: '60px',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    minWidth: '350px',
                                    border: 'none',
                                    backgroundColor: 'rgb(0, 187, 25)',
                                  }}
                                >
                                  {saving 
                                    ? 'Ödeme Sayfası Hazırlanıyor...'
                                    : selectedProduct
                                      ? (() => {
                                          const finalPrice = appliedCoupon 
                                            ? appliedCoupon.finalAmount * 1.20
                                            : (newUserCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth) * 1.20;
                                          return `₺${finalPrice.toFixed(2)} - ÖDEME YAP`;
                                        })()
                                      : !selectedProduct 
                                        ? `Paket Seçin - ÖDEME YAP `
                                        : `Fatura Adresi Belirleyin - ÖDEME YAP`
                                  }
                                </Button>
                              </div>
                            </div>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </TabPane>
              )}

            </Tabs>

            {renderFormActions()}
          </Form>
        </Card>
      </div>
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <FooterSection />
        </div>
        

    </div>
  );
};

export default CompanySettingsPage; 